import { sanitizeUrl } from './sanitize';

const hasContent = field => typeof field?.content === 'string' && field.content.length > 0;
const isNonEmptyString = value => typeof value === 'string' && value.length > 0;

const toVariant = variant => {
  const { url, width, height } = variant || {};
  const isValid = isNonEmptyString(url) && typeof width === 'number' && typeof height === 'number';
  return isValid ? { url: sanitizeUrl(url), width, height } : null;
};

// Same shape as PageBuilder's exposeOpenGraphData, which src/components/Page/Page.js expects
const getSocialSharing = socialSharing => {
  const { title, description, image } = socialSharing || {};
  const variants = image?.type === 'imageAsset' ? image.attributes?.variants || {} : {};
  const image1200 = toVariant(variants.social1200);
  const image600 = toVariant(variants.social600);
  return {
    title: isNonEmptyString(title) ? title : null,
    description: isNonEmptyString(description) ? description : null,
    images1200: image1200 ? [image1200] : null,
    images600: image600 ? [image600] : null,
  };
};

const toPhotoVariants = variants =>
  Object.values(variants || {})
    .filter(v => typeof v?.url === 'string' && typeof v?.width === 'number')
    .map(v => ({ url: sanitizeUrl(v.url), width: v.width }))
    .sort((a, b) => a.width - b.width);

/**
 * Pick the background photo of the first Console section of the given type.
 *
 * Coded marketing pages keep their layout and copy in code but take photos from their Console
 * page asset, so photos can be swapped in Console without a deploy.
 *
 * @param {Array<Object>} sections sections of a Console page asset
 * @param {string} sectionType e.g. 'hero' or 'features'
 * @returns {Array<{ url: string, width: number }>} image variants, narrowest first; empty if none
 */
export const getSectionPhoto = (sections, sectionType) => {
  const section = (sections || []).find(
    s => s?.sectionType === sectionType && s?.appearance?.backgroundImage
  );
  return toPhotoVariants(section?.appearance?.backgroundImage?.attributes?.variants);
};

/**
 * Pick the image of the first block, in a section of the given type, that has one.
 *
 * @param {Array<Object>} sections sections of a Console page asset
 * @param {string} sectionType e.g. 'features'
 * @returns {Array<{ url: string, width: number }>} image variants, narrowest first; empty if none
 */
export const getBlockPhoto = (sections, sectionType) => {
  const blocks = (sections || [])
    .filter(s => s?.sectionType === sectionType)
    .flatMap(s => s.blocks || []);
  const block = blocks.find(b => b?.media?.image?.attributes?.variants);
  return toPhotoVariants(block?.media?.image?.attributes?.variants);
};

/**
 * Read the page title, description and social sharing data from a Console page asset's meta.
 *
 * @param {Object} meta meta of the page asset
 * @returns {{ title: string?, description: string?, socialSharing: Object? }}
 */
export const getPageMeta = meta => {
  const { pageTitle, pageDescription, socialSharing } = meta || {};
  return {
    title: hasContent(pageTitle) ? pageTitle.content : null,
    description: hasContent(pageDescription) ? pageDescription.content : null,
    socialSharing: socialSharing ? getSocialSharing(socialSharing) : null,
  };
};

/**
 * Build schema.org FAQPage data from the questions shown on the page.
 *
 * @param {Array<{ question: string, answer: string }>} faqs
 * @returns {Object} FAQPage schema
 */
export const getFaqSchema = faqs => ({
  '@context': 'http://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: { '@type': 'Answer', text: answer },
  })),
});
